import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from "@angular/common/http";
import {Observable} from "rxjs";
import {ServerEndpoints} from "../../../../server.endpoints";
import {ShiftPlanCalendar, ShiftPlanDay} from "../../_models/shift-plan/shift-plan";
import {DayKey} from "../../../../_shared/time/company-day";

/**
 * The shift plan's ("Dienstplan") one backend door (#409).
 *
 * The screen used to read from six endpoints across four controllers, each gated by a different inherited
 * attribute — which is why #280 could add `ShiftLead` to the shift plan's *writes* and leave every *read*
 * answering 403. `ShiftPlanController` is one controller with one gate; this service is its only client.
 *
 * It serves every caller, not just shift leads: a path only one role takes is a path only one role tests, and
 * the two halves would drift apart again the next time a role is added.
 *
 * Days travel as plain `yyyy-MM-dd` COMPANY calendar days (#645): the server resolves them in the company's time
 * zone, so the browser's own midnight never decides which day (or month) is read.
 *
 * The old services (`EventHttpService`, `LocationHttpService`, `SettingsHttpService`, `EmployeeHttpService`)
 * stay exactly as they are — other screens still use them.
 */
@Injectable({
  providedIn: 'root'
})
export class ShiftPlanHttpService {

  constructor(@Inject("BASE_URL") private baseUrl: string, private http: HttpClient) {
  }

  /**
   * Everything the board needs for one calendar day.
   *
   * @param date the selected company day (`yyyy-MM-dd`).
   */
  getDay(date: DayKey): Observable<ShiftPlanDay> {
    return this.http.get<ShiftPlanDay>(`${this.baseUrl}${ServerEndpoints.Company_ShiftPlan_Day__v2026_8}`,
      {params: new HttpParams({fromObject: {date}})});
  }

  /**
   * The event markers behind the small month calendar.
   *
   * @param start first company day the calendar is showing (`yyyy-MM-dd`).
   * @param end last company day the calendar is showing (`yyyy-MM-dd`, INCLUSIVE — the client adds no day).
   */
  getCalendar(start: DayKey, end: DayKey): Observable<ShiftPlanCalendar> {
    return this.http.get<ShiftPlanCalendar>(`${this.baseUrl}${ServerEndpoints.Company_ShiftPlan_Calendar__v2026_8}`,
      {params: new HttpParams({fromObject: {start, end}})});
  }
}
